import { TabOrientation, TabStyle } from './tab-types';
import { DefaultTheme, ThemeProps } from 'styled-components';
import { ITabsPanelTheme } from './theme/types';

export const isHorizontal = (tabOrientation?: TabOrientation) => tabOrientation === 'horizontal';

export const isVertical = (tabOrientation: TabOrientation) => tabOrientation === 'vertical';

export const isConventional = (tabStyle: TabStyle) => tabStyle === 'conventional';

export const isLine = (tabStyle: TabStyle) => tabStyle === 'line';

export const getTabsPanelTheme = (theme: DefaultTheme, tabStyle: TabStyle) => (isLine(tabStyle) ? theme.tabsPanelLine : theme.tabsPanelConventional);

interface ITabThemeProviderProps extends ThemeProps<DefaultTheme> {
    tabStyle: TabStyle;
}

export function selectTabsPanelTheme<TP extends ITabThemeProviderProps>(
    accessor: (theme: ITabsPanelTheme, isLineStyle: boolean, props: TP) => any): (props: TP) => any {

    return (props) => {
        const { tabStyle, theme } = props;
        const isCurrentStyleLine = isLine(tabStyle);
        const tabTheme = isCurrentStyleLine ? theme.tabsPanelLine :
            theme.tabsPanelConventional;
        return accessor(tabTheme, isCurrentStyleLine, props);
    };
}

export function tabsPanelThemeFrom<TP extends ITabThemeProviderProps>(props: TP) {
    const { tabStyle, theme } = props;
    const isCurrentStyleLine = isLine(tabStyle);
    return isCurrentStyleLine ? theme.tabsPanelLine :
        theme.tabsPanelConventional;
}

export interface ITabStyleBasePropRequirements extends ThemeProps<DefaultTheme> {
    tabStyle: TabStyle;
    tabOrientation: TabOrientation;
}

export type TabStyleFuncMap<P extends ITabStyleBasePropRequirements> = {
    [key: string]: (t: ITabsPanelTheme, props: P) => any;
};

export interface ITabTypeStyleFuncMap<T> {
    line: T;
    conventional: T;
}

type FragmentStyleSelector<P> = (pick: string, condition?: (props: P) => boolean) => any;

export function createTabStyleFragmentSelector<
    P extends ITabStyleBasePropRequirements,
    T extends TabStyleFuncMap<P> = TabStyleFuncMap<P>>(store: ITabTypeStyleFuncMap<T>): FragmentStyleSelector<P> {
    return (pick: keyof T, condition?: (props: P) => boolean) => (props: P) => {
            if (condition && !condition(props)) {
                return;
            }
            const tabStyles = isLine(props.tabStyle) ? store.line : store.conventional;
            const tabTheme = isLine(props.tabStyle) ? props.theme.tabsPanelLine :
                props.theme.tabsPanelConventional;
            const styleFunc = (tabStyles as any)[pick];
            return styleFunc?.call(tabStyles, tabTheme, props);
        };
}
